import React from 'react';
import SEO from '../../components/SEO';
import { useLang } from '../../i18n/LanguageContext';
import { documentLegal } from './contenu';

/**
 * Rendu unique des quatre pages légales (CGV, confidentialité, mentions
 * légales, sécurité des paiements).
 *
 * Avant le 27/08/2026, chacune était un composant de 100 à 120 lignes de JSX
 * avec son texte français écrit en dur. Résultat : un acheteur allemand qui
 * cliquait « Allgemeine Geschäftsbedingungen » depuis un pied de page traduit
 * recevait le droit de rétractation en français — pour un vendeur qui livre en
 * Belgique, aux Pays-Bas, en Allemagne, en Espagne et au Luxembourg, ce n'est
 * pas un détail de confort.
 *
 * Le texte vit désormais dans `contenu/<langue>.js`, une langue par fichier :
 * ajouter une langue, c'est ajouter un fichier, sans toucher au rendu.
 *
 * `dangerouslySetInnerHTML` est employé à dessein : ces contenus sont des
 * modules du dépôt, jamais une saisie d'utilisateur. C'est ce qui permet de
 * garder un lien ou un `<strong>` au milieu d'une phrase sans réinventer un
 * langage de balisage. Aucune donnée extérieure ne doit jamais entrer ici.
 */
export default function PageLegale({ cle }) {
  const { lang } = useLang();
  const doc = documentLegal(cle, lang);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title={doc.seoTitre}
        description={doc.seoDescription}
        url={doc.url}
        keywords={doc.motsCles}
      />
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 break-words">
          {doc.titre}
        </h1>
        <p className="text-sm text-gray-500 mb-2">{doc.maj}</p>

        {/* Le français reste la version de référence : une traduction de
            confort ne doit pas laisser croire qu'elle fait foi devant un
            juge. La ligne n'apparaît donc que sur les autres langues. */}
        {doc.mentionTraduction && (
          <p className="text-xs text-gray-500 italic mb-8 border-l-4 border-gray-200 pl-3">
            {doc.mentionTraduction}
          </p>
        )}
        {!doc.mentionTraduction && <div className="mb-8" />}

        <div className="space-y-8">
          {doc.sections.map((section, i) => (
            <section key={i} className="space-y-4">
              <h2 className="text-2xl font-semibold text-gray-900 break-words">{section.titre}</h2>
              {section.blocs.map((bloc, j) => {
                if (bloc.encadre) {
                  return (
                    <div key={j} className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-500">
                      <p
                        className="text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: bloc.encadre }}
                      />
                    </div>
                  );
                }
                if (bloc.liste) {
                  return (
                    <ul key={j} className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
                      {bloc.liste.map((item, k) => (
                        <li key={k} dangerouslySetInnerHTML={{ __html: item }} />
                      ))}
                    </ul>
                  );
                }
                return (
                  <p
                    key={j}
                    className="text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: bloc.p }}
                  />
                );
              })}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
